<template>
  <div class="xcontainer">
    <plugin-index action="getPluginsByDeveloperId" :request-data="requestData">
      <template #header>
        <div class="developer-card flex py-6 items-center">
          <template v-if="!loading && developer && developer.photoUrl">
            <div class="photo mr-6 rounded-full bg-gray-200 w-36 h-36">
              <img
                :alt="developer.developerName + ' icon'"
                class="rounded-full"
                :src="developer.photoUrl"
              />
            </div>
          </template>

          <div class="developer-details flex content-center">
            <div class="developer-details-content self-center">
              <template v-if="loading || !developer">
                <c-spinner />
              </template>
              <template v-else>
                <template v-if="developer.developerName">
                  <h1 class="mt-0 border-b-0">
                    {{ developer.developerName }}
                  </h1>
                </template>

                <template v-if="developer.location">
                  <ul class="mt-2">
                    <li>{{ developer.location }}</li>
                  </ul>
                </template>

                <!-- Partner badges -->
                <template
                  v-if="
                    developer.partnerInfo &&
                    (developer.partnerInfo.isCraftVerified ||
                      developer.partnerInfo.isCommerceVerified ||
                      developer.partnerInfo.isEnterpriseVerified)
                  "
                >
                  <div class="mt-4 text-sm">
                    <ul
                      class="xl:flex space-y-2 xl:space-y-0 xl:space-x-6 text-gray-600"
                    >
                      <template
                        v-if="
                          developer.partnerInfo &&
                          developer.partnerInfo.isCraftVerified
                        "
                      >
                        <li class="flex items-center">
                          <partner-badge kind="craft" class="shrink-0 mr-2" />
                          Craft Verified
                        </li>
                      </template>
                      <template
                        v-if="
                          developer.partnerInfo &&
                          developer.partnerInfo.isCommerceVerified
                        "
                      >
                        <li class="flex items-center">
                          <partner-badge
                            kind="commerce"
                            class="shrink-0 mr-2"
                          />
                          Craft Commerce Verified
                        </li>
                      </template>
                      <template
                        v-if="
                          developer.partnerInfo &&
                          developer.partnerInfo.isEnterpriseVerified
                        "
                      >
                        <li class="flex items-center">
                          <partner-badge
                            kind="enterprise"
                            class="shrink-0 mr-2"
                          />
                          Enterprise Verified
                        </li>
                      </template>
                    </ul>
                  </div>
                </template>

                <!-- Developer URL and partner profile URL-->
                <ul class="developer-buttons mt-4">
                  <!-- Developer URL -->
                  <template v-if="developer.developerUrl">
                    <li class="inline-block mr-2">
                      <c-btn
                        class="developer-website inline-block"
                        target="_blank"
                        :href="developer.developerUrl"
                      >
                        {{ 'Website' }}
                        <c-icon
                          icon="external-link"
                          class="w-3 ml-1"
                          :size="null"
                        />
                      </c-btn>
                    </li>
                  </template>

                  <!-- Partner profile URL -->
                  <template
                    v-if="
                      developer.partnerInfo && developer.partnerInfo.profileUrl
                    "
                  >
                    <li class="inline-block mr-2">
                      <c-btn
                        class="developer-partner-profile inline-block"
                        target="_blank"
                        :href="developer.partnerInfo.profileUrl"
                      >
                        {{ 'Partner Profile' }}
                        <c-icon
                          icon="external-link"
                          class="w-3 ml-1"
                          :size="null"
                        />
                      </c-btn>
                    </li>
                  </template>
                </ul>
              </template>
            </div>
          </div>
        </div>
      </template>
    </plugin-index>
  </div>
</template>

<script setup lang="ts">
  import {useRoute} from 'vue-router';
  import PluginIndex from '../../../components/PluginIndex.vue';
  import PartnerBadge from '../../../components/partner/PartnerBadge.vue';

  import {
    computed,
    ref,
    definePageMeta,
    useAsyncData,
    usePluginStoreStore,
    navigateTo,
    useAppStore,
    usePluginIndexStore,
  } from '#imports';

  definePageMeta({
    layout: 'site',
  });

  const route = useRoute();
  const pluginStoreStore = usePluginStoreStore();
  const appStore = useAppStore();
  const pluginIndexStore = usePluginIndexStore();

  const loading = ref(false);
  const developer = computed(() => pluginStoreStore.developer);
  const requestData = computed(() => {
    return {
      developerId: pluginStoreStore.developer?.id,
    };
  });

  await useAsyncData(async () => {
    const developerSlug = route.params.slug as string;

    await pluginStoreStore.getDeveloperBySlug(developerSlug).then(async () => {
      const developerSlugToInt = parseInt(developerSlug);
      const developerSlugIsInt =
        developerSlugToInt.toString() === developerSlug;

      if (developerSlugIsInt) {
        navigateTo(
          {
            path: '/developer/' + pluginStoreStore.developer?.slug,
            query: route.query,
          },
          {
            redirectCode: 301,
          },
        );
      }

      appStore.updatePageMeta({
        title: developer.value?.developerName,
        description: developer.value?.developerName + ' developer.',
      });

      await pluginIndexStore
        .getPluginsByDeveloperId({
          developerId: developer.value?.id,
        })
        .catch((thrown) => {
          throw createError({message: thrown});
        });
    });
  });
</script>
